/**
 * @file api.ts
 * @brief TanStack mutations for the signed-in user's self-service
 *        profile surfaces.
 *
 * @details
 * Three thin mutation hooks that wrap the same `/api/auth/*` endpoints
 * the mobile app already uses:
 *
 *   useUpdateProfile        → PUT  /api/auth/profile
 *   useChangeEmail          → PUT  /api/auth/profile (email + re-auth, B34)
 *   useUploadProfileImage   → POST /api/auth/upload-profile-image
 *   useChangePassword       → PUT  /api/auth/change-password
 *   useDeleteAccount        → DELETE /api/auth/account
 *   useDownloadMyData       → GET    /api/auth/export (zip)
 *   useEmailMyData          → POST   /api/auth/export/email (ADR-046)
 *
 * The image-upload endpoint expects a base-64 payload — the helper
 * `fileToBase64` here does the FileReader dance so the component
 * stays declarative. After every successful mutation we kick the
 * AuthContext's `refreshUser()` so the new `profileImageUrl` /
 * `firstName` / etc are reflected in the topbar avatar without a
 * page reload.
 *
 * @dependencies
 * - @tanstack/react-query
 * - shared/api/client
 * - shared/api/endpoints
 *
 * @references
 * - BACKEND/src/features/auth/routes.js (PUT /profile, POST /upload-profile-image, PUT /change-password)
 *
 * @author DES
 * @date 26-05-2026
 */

import { useMutation } from "@tanstack/react-query";
import { ApiClientError, apiClient, apiDelete, apiPost, apiPut } from "@/shared/api/client";
import { authEndpoints } from "@/shared/api/endpoints";
import { AuthUserSchema, type AuthUser } from "@/shared/types/api";

// ----------------------------------------------------------------------
// Profile update — name / phone / gender etc. Backend ignores fields
// not in its own validation schema, so we feed the union of what the
// panel currently exposes; future fields just need an entry here.
// ----------------------------------------------------------------------
export interface UpdateProfileInput {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  gender?: string;
}

export function useUpdateProfile() {
  return useMutation<AuthUser, Error, UpdateProfileInput>({
    mutationFn: async (input) => {
      const res = await apiPut<{ success: boolean; user: unknown }>(authEndpoints.profile, input);
      return AuthUserSchema.parse(res.user);
    },
  });
}

// ----------------------------------------------------------------------
// Email change (B34, 26-10-2026): its own save with only the email and the
// proof, so a refused re-auth never loses other edits. Proof: the password
// (password accounts) or, after 403 REAUTH_REQUIRED with `twoFactorAllowed`,
// a code. Errors (REAUTH_REQUIRED 403, INVALID_PASSWORD /
// TWO_FACTOR_INVALID_CODE 400, TWO_FACTOR_LOCKED 429, EMAIL_EXISTS 409,
// EMAIL_NOTICE_FAILED 503) arrive as ApiClientError; none signs out.
// ----------------------------------------------------------------------
export interface ChangeEmailInput {
  email: string;
  currentPassword?: string;
  twoFactorCode?: string;
  backupCode?: string;
}

export function useChangeEmail() {
  return useMutation<AuthUser, Error, ChangeEmailInput>({
    mutationFn: async (input) => {
      const res = await apiPut<{ success: boolean; user: unknown }>(authEndpoints.profile, input);
      return AuthUserSchema.parse(res.user);
    },
  });
}

// ----------------------------------------------------------------------
// Image upload — converts the picked File to a base-64 data URL and
// posts it to /api/auth/upload-profile-image. Backend strips the data
// URL prefix, persists, and returns the new signed read URL.
// ----------------------------------------------------------------------
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("Read failed"));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsDataURL(file);
  });
}

export interface UploadProfileImageResult {
  imageUrl: string;
}

export function useUploadProfileImage() {
  return useMutation<UploadProfileImageResult, Error, File>({
    mutationFn: async (file) => {
      const dataUrl = await fileToBase64(file);
      const res = await apiPost<{ success: boolean; imageUrl: string }>(
        authEndpoints.uploadProfileImage,
        { imageData: dataUrl, contentType: file.type || "image/jpeg" },
      );
      return { imageUrl: res.imageUrl };
    },
  });
}

// ----------------------------------------------------------------------
// Password change
// ----------------------------------------------------------------------
export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

export function useChangePassword() {
  return useMutation<{ success: true }, Error, ChangePasswordInput>({
    mutationFn: async (input) => {
      await apiPut<unknown>(authEndpoints.changePassword, input);
      return { success: true };
    },
  });
}

// ----------------------------------------------------------------------
// Account deletion (GDPR Art 17, 05-10-2026). Same endpoint as the phone.
// `confirm` is the account email. `currentPassword` stays empty for
// Google/Apple/Microsoft accounts, which have none; the backend decides which
// check applies. Company accounts get COMPANY_USER_CANNOT_DELETE (403).
// ADR-046 (19-10-2026): answers DELETION_STARTED with the sentence to show and
// `erasureDueAt`; a social account signed in over 10 minutes ago gets
// 403 REAUTH_REQUIRED (not 401, so the client does not sign out).
// B33 (26-10-2026): with 2FA on, that answer has `twoFactorAllowed` and the
// request may be sent again with `twoFactorCode` or `backupCode`.
// ----------------------------------------------------------------------
export interface DeleteAccountInput {
  currentPassword: string;
  confirm: string;
  twoFactorCode?: string;
  backupCode?: string;
}

export interface DeleteAccountResult {
  message: string;
  erasureDueAt: string | null;
}

export function useDeleteAccount() {
  return useMutation<DeleteAccountResult, Error, DeleteAccountInput>({
    mutationFn: async (input) => {
      const res = await apiDelete<{ message?: string; erasureDueAt?: string | null }>(
        authEndpoints.deleteAccount,
        { data: input },
      );
      return {
        message:
          res?.message || "We've started deleting your account; it will be done within 30 days.",
        erasureDueAt: res?.erasureDueAt ?? null,
      };
    },
  });
}

// ----------------------------------------------------------------------
// Email me my data (ADR-046, 19-10-2026): the export plus the PDFs, stored
// for 8 days and emailed to the account address as a 7-day link. Built
// inside the request, so big accounts take up to a minute: longer timeout
// than the client's 30 s. 429 RATE_LIMITED after 3 an hour (shared with
// Download); 403 COMPANY_USER_CANNOT_EXPORT for company employees.
// ----------------------------------------------------------------------
// 502 EXPORT_EMAIL_FAILED: the archive was built but the mail server refused
// the address; the body carries `downloadUrl` (7-day link), so the user gets
// the link on screen instead of an error.
export interface EmailMyDataResult {
  message: string;
  expiresAt: string | null;
  /** Set only when the email failed and the server handed back the link. */
  downloadUrl: string | null;
}

export function useEmailMyData() {
  return useMutation<EmailMyDataResult, Error, void>({
    mutationFn: async () => {
      try {
        const res = await apiPost<{ message?: string; expiresAt?: string | null }>(
          authEndpoints.exportDataEmail,
          undefined,
          { timeout: 150_000 },
        );
        return {
          message: res?.message || "We've emailed you a link to download your data.",
          expiresAt: res?.expiresAt ?? null,
          downloadUrl: null,
        };
      } catch (err) {
        if (err instanceof ApiClientError && err.code === "EXPORT_EMAIL_FAILED") {
          const body = (err.raw as {
            response?: { data?: { downloadUrl?: unknown; expiresAt?: unknown } };
          })?.response?.data;
          if (typeof body?.downloadUrl === "string" && body.downloadUrl) {
            return {
              message: err.message,
              expiresAt: typeof body.expiresAt === "string" ? body.expiresAt : null,
              downloadUrl: body.downloadUrl,
            };
          }
        }
        throw err;
      }
    },
  });
}

// ----------------------------------------------------------------------
// Download my data (GDPR Art 15/20, 05-10-2026): a zip of JSON files plus
// 7-day links to recordings and PDFs. The request asks for a blob, so an
// error body also arrives as a blob; read it back for the server's message.
// ----------------------------------------------------------------------
async function messageFromBlobError(err: unknown): Promise<Error> {
  if (!(err instanceof ApiClientError))
    return err instanceof Error ? err : new Error("Download failed");
  const data = (err.raw as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const body = JSON.parse(await data.text()) as {
        message?: string;
        error?: string;
        code?: string;
      };
      const message = body.message || body.error;
      if (message) return new ApiClientError(message, err.status, body.code, err.raw);
    } catch {
      // not JSON: keep the transport message
    }
  }
  return err;
}

/**
 * GET a data-export zip and save it. Shared by "Download my data" and the
 * Employees row action (company admin downloads one employee's data).
 */
export async function downloadExportZip(
  url: string,
  /** Used when the browser cannot read Content-Disposition (cross-origin). */
  fallbackName = `verblizr-data-${new Date().toISOString().slice(0, 10)}.zip`,
): Promise<{ filename: string }> {
  try {
    const res = await apiClient.get(url, { responseType: "blob" });
    const disposition = String(res.headers["content-disposition"] ?? "");
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;
    const blobUrl = URL.createObjectURL(res.data as Blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
    return { filename };
  } catch (err) {
    throw await messageFromBlobError(err);
  }
}

export function useDownloadMyData() {
  return useMutation<{ filename: string }, Error, void>({
    mutationFn: () => downloadExportZip(authEndpoints.exportData),
  });
}
